export type Language = 'fr' | 'en' | 'es';

export const translations = {
  fr: {
    meta: {
      title: 'SchemasGen - Générateur de Données Structurées',
      description: 'Générateur de données structurées JSON-LD gratuit : analysez n\'importe quelle URL et obtenez des schémas construits à partir du vrai contenu de la page. Aucune donnée inventée, sans inscription.',
    },
    header: {
      title: 'SchemasGen',
      heroTitle: 'Générateur de Données Structurées',
      heroHighlight: 'par IA',
      subtitle: 'Générez et optimisez vos données structurées',
      schemaOrg: '',
      automatically: 'automatiquement.',
      description: 'Analysez n\'importe quelle URL et obtenez du JSON-LD prêt à l\'emploi.',
      badge: 'Détection intelligente du type de page',
      trust: [
        { title: 'Aucune donnée inventée', desc: 'Uniquement les faits présents sur la page' },
        { title: 'Code vérifié', desc: 'Testable dans le validateur officiel' },
        { title: 'Essai gratuit sans inscription', desc: '3 analyses/jour, + 5 crédits offerts avec un compte gratuit' },
      ],
    },
    urlInput: {
      placeholder: 'Entrez une URL à analyser...',
      button: 'Analyser',
      analyzing: 'Analyse en cours...',
    },
    emptyState: {
      title: 'Prêt à analyser votre page',
      description: 'Entrez une URL ci-dessus pour détecter et générer les schemas appropriés',
    },
    schemaTypes: {
      product: { label: 'Product', desc: 'Pages produits' },
      organization: { label: 'Organization', desc: 'Pages d\'accueil' },
      faq: { label: 'FAQPage', desc: 'Questions fréquentes' },
      article: { label: 'Article', desc: 'Articles de blog' },
      event: { label: 'Event', desc: 'Événements' },
      breadcrumb: { label: 'BreadcrumbList', desc: 'Fil d\'Ariane' },
    },
    results: {
      analysisComplete: 'Analyse terminée',
      schemasGenerated: 'schema(s) généré(s)',
      generatedSchemas: 'Schemas générés',
      existingSchemas: 'Schemas existants',
      suggestedSchemas: 'Schemas suggérés',
      copyCode: 'Copier le code',
      copied: 'Copié !',
      new: 'Nouveau',
      improved: 'Amélioré',
    },
    faq: {
      title: 'Questions Fréquentes - Données Structurées et SEO',
      questions: [
        {
          question: 'Qu\'est-ce que les données structurées ?',
          answer: 'Les données structurées sont un format standardisé (JSON-LD) qui permet aux moteurs de recherche de mieux comprendre le contenu de vos pages. Elles décrivent précisément les entités (produits, articles, événements, FAQ) présentes sur votre site, améliorant ainsi votre visibilité dans les résultats de recherche.',
        },
        {
          question: 'Pourquoi les données structurées sont-elles importantes pour le SEO ?',
          answer: 'Les données structurées permettent d\'obtenir des résultats enrichis (rich snippets) dans Google : étoiles de notation, prix, disponibilité, FAQ déroulantes, etc. Ces éléments rendent vos résultats plus visibles et peuvent améliorer votre taux de clic.',
        },
        {
          question: 'Comment SchemasGen aide-t-il mon référencement naturel ?',
          answer: 'SchemasGen analyse automatiquement le contenu de vos pages et génère des schemas JSON-LD optimisés avec les vraies données de votre site. Plus besoin de coder manuellement : copiez-collez le code généré dans votre HTML et profitez immédiatement des avantages SEO.',
        },
        {
          question: 'Quels types de schemas sont supportés ?',
          answer: 'SchemasGen supporte tous les principaux types de schemas : Product, Article, FAQPage, Organization, LocalBusiness, Event, Recipe, BreadcrumbList, Review, et bien plus. L\'outil détecte automatiquement le type de page et génère les schemas les plus pertinents.',
        },
        {
          question: 'Qu\'est-ce que le GEO (Generative Engine Optimization) ?',
          answer: 'Le GEO est l\'optimisation pour les moteurs de recherche génératifs comme Google SGE, Bing Copilot ou ChatGPT. Les données structurées jouent un rôle crucial car elles fournissent des informations précises et structurées que ces IA peuvent facilement extraire et utiliser dans leurs réponses.',
        },
        {
          question: 'Comment les données structurées impactent-elles le GEO ?',
          answer: 'Les moteurs génératifs privilégient les sources avec des données structurées claires car elles facilitent l\'extraction d\'informations fiables. Un site avec des schemas bien implémentés a plus de chances d\'être cité dans les réponses générées par l\'IA.',
        },
      ],
    },
    footer: {
      description: 'Générez des données structurées optimisées pour le SEO',
      terms: 'CGV',
      privacy: 'Confidentialité',
    },
    errors: {
      title: 'Erreur',
      analysisFailed: 'Impossible d\'analyser cette URL. Veuillez réessayer.',
    },
    auth: {
      subtitle: 'Connectez-vous pour générer des données structurées',
      signIn: 'Connexion',
      signUp: 'Inscription',
      signInButton: 'Se connecter',
      signUpButton: 'Créer un compte',
      signOut: 'Déconnexion',
      continueWithGoogle: 'Continuer avec Google',
      or: 'ou',
      email: 'Email',
      password: 'Mot de passe',
      fullName: 'Nom complet',
      error: 'Erreur',
      success: 'Succès',
      invalidCredentials: 'Email ou mot de passe incorrect',
      emailNotConfirmed: 'Veuillez confirmer votre email avant de vous connecter',
      userExists: 'Un compte existe déjà avec cet email',
      checkEmail: 'Vérifiez votre email pour confirmer votre inscription',
      unexpectedError: 'Une erreur inattendue s\'est produite',
      googleError: 'Erreur lors de la connexion avec Google',
      terms: 'En continuant, vous acceptez nos conditions d\'utilisation',
      welcome: 'Bienvenue',
    },
    account: {
      title: 'Mon Compte',
      subtitle: 'Gérez votre profil et vos paramètres',
      profile: 'Profil',
      history: 'Historique',
      avatar: 'Photo de profil',
      avatarDescription: 'Cliquez sur votre avatar pour le modifier',
      avatarHint: 'JPG, PNG ou GIF. Max 2MB.',
      avatarUpdated: 'Avatar mis à jour avec succès',
      personalInfo: 'Informations personnelles',
      personalInfoDescription: 'Vos informations de base',
      fullName: 'Nom complet',
      fullNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      bio: 'Biographie',
      bioPlaceholder: 'Parlez-nous de vous...',
      socialLinks: 'Liens sociaux',
      socialLinksDescription: 'Votre site web et réseaux sociaux',
      website: 'Site web',
      saveChanges: 'Enregistrer',
      profileUpdated: 'Profil mis à jour avec succès',
      analysisHistory: 'Historique des analyses',
      analysisHistoryDescription: 'Toutes vos analyses passées',
      noHistory: 'Aucune analyse effectuée pour le moment',
      analysisDeleted: 'Analyse supprimée',
      error: 'Erreur',
      success: 'Succès',
      invalidImage: 'Veuillez sélectionner une image valide',
      uploadError: 'Erreur lors du téléchargement',
      updateError: 'Erreur lors de la mise à jour',
      deleteError: 'Erreur lors de la suppression',
      myAccount: 'Mon compte',
    },
  },
  en: {
    meta: {
      title: 'SchemasGen - Structured Data Generator',
      description: 'Free schema markup generator: analyze any URL and get JSON-LD structured data built from the page\'s real content. No invented data, no signup.',
    },
    header: {
      title: 'SchemasGen',
      heroTitle: 'AI-Powered Structured Data',
      heroHighlight: 'Generator',
      subtitle: 'Generate and optimize your structured data',
      schemaOrg: '',
      automatically: 'automatically.',
      description: 'Analyze any URL and get ready-to-use JSON-LD.',
      badge: 'Smart page type detection',
      trust: [
        { title: 'No invented data', desc: 'Only facts found on the page' },
        { title: 'Verified code', desc: 'Testable in the official validator' },
        { title: 'Free trial, no signup', desc: '3 analyses/day, + 5 bonus credits with a free account' },
      ],
    },
    urlInput: {
      placeholder: 'Enter a URL to analyze...',
      button: 'Analyze',
      analyzing: 'Analyzing...',
    },
    emptyState: {
      title: 'Ready to analyze your page',
      description: 'Enter a URL above to detect and generate appropriate schemas',
    },
    schemaTypes: {
      product: { label: 'Product', desc: 'Product pages' },
      organization: { label: 'Organization', desc: 'Home pages' },
      faq: { label: 'FAQPage', desc: 'FAQ pages' },
      article: { label: 'Article', desc: 'Blog posts' },
      event: { label: 'Event', desc: 'Events' },
      breadcrumb: { label: 'BreadcrumbList', desc: 'Breadcrumbs' },
    },
    results: {
      analysisComplete: 'Analysis complete',
      schemasGenerated: 'schema(s) generated',
      generatedSchemas: 'Generated Schemas',
      existingSchemas: 'Existing Schemas',
      suggestedSchemas: 'Suggested Schemas',
      copyCode: 'Copy code',
      copied: 'Copied!',
      new: 'New',
      improved: 'Improved',
    },
    faq: {
      title: 'FAQ - Structured Data and SEO',
      questions: [
        {
          question: 'What is structured data?',
          answer: 'Structured data is a standardized format (JSON-LD) that helps search engines better understand your page content. It precisely describes entities (products, articles, events, FAQs) on your site, improving your visibility in search results.',
        },
        {
          question: 'Why is structured data important for SEO?',
          answer: 'Structured data enables rich results (rich snippets) in Google: star ratings, prices, availability, expandable FAQs, etc. These elements make your results stand out and can improve your click-through rate.',
        },
        {
          question: 'How does SchemasGen help my SEO?',
          answer: 'SchemasGen automatically analyzes your page content and generates optimized JSON-LD schemas with real data from your site. No manual coding needed: copy-paste the generated code into your HTML and immediately benefit from SEO advantages.',
        },
        {
          question: 'What types of schemas are supported?',
          answer: 'SchemasGen supports all major schema types: Product, Article, FAQPage, Organization, LocalBusiness, Event, Recipe, BreadcrumbList, Review, and many more. The tool automatically detects the page type and generates the most relevant schemas.',
        },
        {
          question: 'What is GEO (Generative Engine Optimization)?',
          answer: 'GEO is optimization for generative search engines like Google SGE, Bing Copilot, or ChatGPT. Structured data plays a crucial role as it provides precise, structured information that these AIs can easily extract and use in their responses.',
        },
        {
          question: 'How does structured data impact GEO?',
          answer: 'Generative engines favor sources with clear structured data because it facilitates reliable information extraction. A site with well-implemented schemas is more likely to be cited in AI-generated responses.',
        },
      ],
    },
    footer: {
      description: 'Generate SEO-optimized structured data',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
    },
    errors: {
      title: 'Error',
      analysisFailed: 'Unable to analyze this URL. Please try again.',
    },
    auth: {
      subtitle: 'Sign in to generate structured data',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      signInButton: 'Sign in',
      signUpButton: 'Create account',
      signOut: 'Sign out',
      continueWithGoogle: 'Continue with Google',
      or: 'or',
      email: 'Email',
      password: 'Password',
      fullName: 'Full name',
      error: 'Error',
      success: 'Success',
      invalidCredentials: 'Invalid email or password',
      emailNotConfirmed: 'Please confirm your email before signing in',
      userExists: 'An account already exists with this email',
      checkEmail: 'Check your email to confirm your registration',
      unexpectedError: 'An unexpected error occurred',
      googleError: 'Error signing in with Google',
      terms: 'By continuing, you agree to our terms of service',
      welcome: 'Welcome',
    },
    account: {
      title: 'My Account',
      subtitle: 'Manage your profile and settings',
      profile: 'Profile',
      history: 'History',
      avatar: 'Profile Picture',
      avatarDescription: 'Click on your avatar to change it',
      avatarHint: 'JPG, PNG or GIF. Max 2MB.',
      avatarUpdated: 'Avatar updated successfully',
      personalInfo: 'Personal Information',
      personalInfoDescription: 'Your basic information',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Enter your name',
      email: 'Email',
      bio: 'Biography',
      bioPlaceholder: 'Tell us about yourself...',
      socialLinks: 'Social Links',
      socialLinksDescription: 'Your website and social networks',
      website: 'Website',
      saveChanges: 'Save Changes',
      profileUpdated: 'Profile updated successfully',
      analysisHistory: 'Analysis History',
      analysisHistoryDescription: 'All your past analyses',
      noHistory: 'No analyses yet',
      analysisDeleted: 'Analysis deleted',
      error: 'Error',
      success: 'Success',
      invalidImage: 'Please select a valid image',
      uploadError: 'Error uploading image',
      updateError: 'Error updating profile',
      deleteError: 'Error deleting analysis',
      myAccount: 'My account',
    },
  },
  es: {
    meta: {
      title: 'SchemasGen - Generador de Datos Estructurados',
      description: 'Generador de datos estructurados JSON-LD gratis: analiza cualquier URL y obtén schemas creados con el contenido real de la página. Sin datos inventados, sin registro.',
    },
    header: {
      title: 'SchemasGen',
      heroTitle: 'Generador de Datos Estructurados',
      heroHighlight: 'con IA',
      subtitle: 'Genera y optimiza tus datos estructurados',
      schemaOrg: '',
      automatically: 'automáticamente.',
      description: 'Analiza cualquier URL y obtén JSON-LD listo para usar.',
      badge: 'Detección inteligente del tipo de página',
      trust: [
        { title: 'Sin datos inventados', desc: 'Solo los hechos presentes en la página' },
        { title: 'Código verificado', desc: 'Comprobable en el validador oficial' },
        { title: 'Prueba gratis sin registro', desc: '3 análisis/día, + 5 créditos de regalo con una cuenta gratuita' },
      ],
    },
    urlInput: {
      placeholder: 'Ingresa una URL para analizar...',
      button: 'Analizar',
      analyzing: 'Analizando...',
    },
    emptyState: {
      title: 'Listo para analizar tu página',
      description: 'Ingresa una URL arriba para detectar y generar los schemas apropiados',
    },
    schemaTypes: {
      product: { label: 'Product', desc: 'Páginas de productos' },
      organization: { label: 'Organization', desc: 'Páginas de inicio' },
      faq: { label: 'FAQPage', desc: 'Preguntas frecuentes' },
      article: { label: 'Article', desc: 'Artículos de blog' },
      event: { label: 'Event', desc: 'Eventos' },
      breadcrumb: { label: 'BreadcrumbList', desc: 'Migas de pan' },
    },
    results: {
      analysisComplete: 'Análisis completado',
      schemasGenerated: 'schema(s) generado(s)',
      generatedSchemas: 'Schemas generados',
      existingSchemas: 'Schemas existentes',
      suggestedSchemas: 'Schemas sugeridos',
      copyCode: 'Copiar código',
      copied: '¡Copiado!',
      new: 'Nuevo',
      improved: 'Mejorado',
    },
    faq: {
      title: 'Preguntas Frecuentes - Datos Estructurados y SEO',
      questions: [
        {
          question: '¿Qué son los datos estructurados?',
          answer: 'Los datos estructurados son un formato estandarizado (JSON-LD) que ayuda a los motores de búsqueda a comprender mejor el contenido de tus páginas. Describen con precisión las entidades (productos, artículos, eventos, FAQ) en tu sitio, mejorando tu visibilidad en los resultados de búsqueda.',
        },
        {
          question: '¿Por qué son importantes los datos estructurados para el SEO?',
          answer: 'Los datos estructurados permiten obtener resultados enriquecidos (rich snippets) en Google: calificaciones con estrellas, precios, disponibilidad, FAQ desplegables, etc. Estos elementos hacen tus resultados más visibles y pueden mejorar tu tasa de clics.',
        },
        {
          question: '¿Cómo ayuda SchemasGen a mi SEO?',
          answer: 'SchemasGen analiza automáticamente el contenido de tus páginas y genera schemas JSON-LD optimizados con los datos reales de tu sitio. Sin necesidad de codificar manualmente: copia y pega el código generado en tu HTML y disfruta inmediatamente de las ventajas SEO.',
        },
        {
          question: '¿Qué tipos de schemas son compatibles?',
          answer: 'SchemasGen soporta todos los tipos principales de schemas: Product, Article, FAQPage, Organization, LocalBusiness, Event, Recipe, BreadcrumbList, Review y muchos más. La herramienta detecta automáticamente el tipo de página y genera los schemas más relevantes.',
        },
        {
          question: '¿Qué es el GEO (Generative Engine Optimization)?',
          answer: 'GEO es la optimización para motores de búsqueda generativos como Google SGE, Bing Copilot o ChatGPT. Los datos estructurados juegan un papel crucial ya que proporcionan información precisa y estructurada que estas IAs pueden extraer fácilmente y usar en sus respuestas.',
        },
        {
          question: '¿Cómo impactan los datos estructurados en el GEO?',
          answer: 'Los motores generativos favorecen fuentes con datos estructurados claros porque facilitan la extracción de información confiable. Un sitio con schemas bien implementados tiene más probabilidades de ser citado en las respuestas generadas por IA.',
        },
      ],
    },
    footer: {
      description: 'Genera datos estructurados optimizados para SEO',
      terms: 'Condiciones',
      privacy: 'Privacidad',
    },
    errors: {
      title: 'Error',
      analysisFailed: 'No se pudo analizar esta URL. Por favor, inténtalo de nuevo.',
    },
    auth: {
      subtitle: 'Inicia sesión para generar datos estructurados',
      signIn: 'Iniciar sesión',
      signUp: 'Registrarse',
      signInButton: 'Iniciar sesión',
      signUpButton: 'Crear cuenta',
      signOut: 'Cerrar sesión',
      continueWithGoogle: 'Continuar con Google',
      or: 'o',
      email: 'Correo electrónico',
      password: 'Contraseña',
      fullName: 'Nombre completo',
      error: 'Error',
      success: 'Éxito',
      invalidCredentials: 'Correo electrónico o contraseña incorrectos',
      emailNotConfirmed: 'Por favor, confirma tu correo antes de iniciar sesión',
      userExists: 'Ya existe una cuenta con este correo',
      checkEmail: 'Revisa tu correo para confirmar tu registro',
      unexpectedError: 'Ocurrió un error inesperado',
      googleError: 'Error al iniciar sesión con Google',
      terms: 'Al continuar, aceptas nuestros términos de servicio',
      welcome: 'Bienvenido',
    },
    account: {
      title: 'Mi Cuenta',
      subtitle: 'Gestiona tu perfil y configuración',
      profile: 'Perfil',
      history: 'Historial',
      avatar: 'Foto de perfil',
      avatarDescription: 'Haz clic en tu avatar para cambiarlo',
      avatarHint: 'JPG, PNG o GIF. Máx 2MB.',
      avatarUpdated: 'Avatar actualizado con éxito',
      personalInfo: 'Información personal',
      personalInfoDescription: 'Tu información básica',
      fullName: 'Nombre completo',
      fullNamePlaceholder: 'Ingresa tu nombre',
      email: 'Correo electrónico',
      bio: 'Biografía',
      bioPlaceholder: 'Cuéntanos sobre ti...',
      socialLinks: 'Enlaces sociales',
      socialLinksDescription: 'Tu sitio web y redes sociales',
      website: 'Sitio web',
      saveChanges: 'Guardar cambios',
      profileUpdated: 'Perfil actualizado con éxito',
      analysisHistory: 'Historial de análisis',
      analysisHistoryDescription: 'Todos tus análisis anteriores',
      noHistory: 'Aún no hay análisis',
      analysisDeleted: 'Análisis eliminado',
      error: 'Error',
      success: 'Éxito',
      invalidImage: 'Por favor selecciona una imagen válida',
      uploadError: 'Error al subir la imagen',
      updateError: 'Error al actualizar el perfil',
      deleteError: 'Error al eliminar el análisis',
      myAccount: 'Mi cuenta',
    },
  },
} as const;

export type TranslationKeys = typeof translations.fr;
